import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import { zodResolver } from "apps/web/src/utils/zodResolver";
import { UseFormReturn, useForm } from "react-hook-form";
import TWItemizedTooltip from "../../../../componentLibrary/TWTooltip/TWItemizedTooltip";
import { GptRecipeFragment, useGetPossibleGptRecipeByNameMutation } from "../../../../types";
import { GPTLoadingContent } from "../GPTLoadingContent";
import { RecipeGptDialogHeader } from "../RecipeGptDialogHeader";
import { GenerateWithGPTContent } from "./GenerateWithGPTContent";
import { GenerateWithGPTFormSchema, GenerateWithGPTFormType, generateWithGPTFormDefaultValues } from "./GenerateWithGPTForm";

interface GenerateWithGPTDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerateComplete: (gptRecipe: GptRecipeFragment, closeModal: () => void) => void;
  onError: (error: any) => void;
  initialName?: string;
  initialServingYield?: number;
}

const getCanSaveTooltips = ({ formState: { errors } }: UseFormReturn<GenerateWithGPTFormType>): string[] => {
  const tooltips: string[] = [];
  const nameError = errors.name?.message;
  if (nameError) {
    tooltips.push(nameError);
  }

  const servingError = errors.servingYield?.message;
  if (servingError) {
    tooltips.push(servingError);
  }

  return tooltips;
};

export const GenerateWithGPTDialog = ({
  open,
  onClose,
  onGenerateComplete,
  onError,
  initialName,
  initialServingYield,
}: GenerateWithGPTDialogProps) => {
  const form = useForm<GenerateWithGPTFormType>({
    defaultValues: generateWithGPTFormDefaultValues(initialName, initialServingYield),
    resolver: zodResolver(GenerateWithGPTFormSchema),
    mode: "all",
  });

  const { formState } = form;
  const { isDirty, touchedFields } = formState;

  const [generateRecipeByName, { loading: generatingRecipe }] = useGetPossibleGptRecipeByNameMutation({
    onCompleted: data => {
      onGenerateComplete(data.getPossibleGPTRecipeByName, onClose);
    },
    onError: e => onError(e),
  });

  const handleClose = () => {
    if (generatingRecipe) {
      return;
    }
    onClose();
  };

  const canSaveTooltips = getCanSaveTooltips(form);
  const isFormTouched = isDirty || Object.keys(touchedFields).length > 0;
  const isGenerateDisabled = canSaveTooltips.length !== 0 || !isFormTouched || generatingRecipe;

  const handleGenerate = form.handleSubmit(({ name, servingYield }) => {
    generateRecipeByName({ variables: { input: { name, servingYield } } });
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxWidth: 680 } }}>
      <RecipeGptDialogHeader
        title="Generate with AI"
        subtitle="Describe the dish and desired serving yields — you'll get a draft you can edit."
        icon={<AutoAwesomeIcon fontSize="small" />}
        onClose={handleClose}
        badgeVariant="indigoSky"
      />
      <DialogContent sx={{ pb: generatingRecipe ? 6 : 3, pt: generatingRecipe ? 6 : 2 }}>
        {generatingRecipe ? <GPTLoadingContent actionText="Generating" /> : <GenerateWithGPTContent form={form} />}
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          m: 0,
          px: 3,
          pt: 2,
          pb: 3,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pr: { sm: 2 } }}>
          Review every generated recipe before sharing with athletes.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0, justifyContent: { xs: "flex-end", sm: "flex-start" } }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <TWItemizedTooltip title="Fix the following before Generating:" items={canSaveTooltips}>
            <span>
              <Button onClick={handleGenerate} disabled={isGenerateDisabled}>
                Generate recipe
              </Button>
            </span>
          </TWItemizedTooltip>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
